import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import { generateUrls } from '@hooks/useTeam'
import type { TeamFilters } from '@hooks/useTeams'
import useTeams from '@hooks/useTeams'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import TeamListItem from '../TeamListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      callbacks: {
        buildSearchRequestOptions: () => ({ admin_users_only: true }),
      },
      component: 'EntitySelector',
      componentProps: {
        entityKey: 'users',
        entityTitleKey: 'full_name',
        style,
      },
      key: 'user_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: TeamFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const teamsPayload = useTeams({
    filters: { ...pageFilters },
    requestOptions: { include_team_user_ids: true },
    performHttpRequests: true,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredTeams,
    filteredTeamsCount,
    hasFilteredTeams,
    loading,
  } = teamsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      showCreateTeamModal,
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { teamsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Teams | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateTeamModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="teams"
        tabBarItems={[
          {
            href: teamsIndexUrl,
            icon: faCheckCircle,
            key: 'teams',
            title: 'Teams',
          },
        ]}
        title="Teams"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTeams && <ListBlankState />}

            {!loading && hasFilteredTeams && (
              <DashboardModule title="Teams">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredTeams.map(team => (
                      <TeamListItem key={team.id} team={team} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredTeamsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
